/**
 * Generates a unique key for the form based on the current entity's ID or a hardcoded default key.
 * This method is crucial for situations where changing entities (with different data expectations for form fields)
 * does not automatically clear previous values due to Formik's internal state management. Specifically, when numeric
 * fields on a form are expected to be `null` for the new entity but were populated with numeric values for the previous
 * entity, lingering values can occur. Changing the key instructs React to re-mount the component, effectively resetting
 * its internal state and ensuring fields like `TextField` for numeric inputs are initialized correctly.
 *
 * Ensures safety by incorporating type checks to handle various types of `currEntity`, including `null`,
 * and defaults to a hardcoded 'defaultFormKey' when no entity is selected.
 *
 * @param {Object | null} currEntity - The current entity object or null if no entity is selected.
 * @returns {string} - A unique key for the form, ensuring component reset on entity change.
 */
export function resetFormUsingKey(
  currEntity: { id: number | string } | null
): string {
  // Check if currEntity is an object and has an 'id' property
  const isEntityValid =
    currEntity !== null && typeof currEntity === 'object' && 'id' in currEntity;

  // Generate the form key based on the currEntity's id, if valid; otherwise, use the hardcoded default key
  return isEntityValid
    ? `entity-${currEntity.id.toString()}`
    : 'defaultFormKey';
}
